import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Hotel, PartyPopper, TreePine, Mountain, Building2, Users, ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/business-type")({
  component: BusinessTypePage,
});

type BusinessType =
  | "hotel_owner"
  | "event_centre_owner"
  | "open_space_owner"
  | "resort_owner"
  | "apartment_owner"
  | "conference_hall_owner";

const OPTIONS: {
  type: BusinessType;
  icon: React.ElementType;
  title: string;
  desc: string;
  accent: string;
  iconBg: string;
  delay: string;
}[] = [
  {
    type: "hotel_owner",
    icon: Hotel,
    title: "Hotel",
    desc: "Manage rooms, accept guest bookings, and grow your hospitality business.",
    accent: "hover:border-primary hover:bg-primary/5",
    iconBg: "bg-primary/10 text-primary",
    delay: "delay-150",
  },
  {
    type: "event_centre_owner",
    icon: PartyPopper,
    title: "Event Centre",
    desc: "Host weddings, conferences, and celebrations in your event hall.",
    accent: "hover:border-violet-500 hover:bg-violet-50/60",
    iconBg: "bg-violet-100 text-violet-600",
    delay: "delay-200",
  },
  {
    type: "open_space_owner",
    icon: TreePine,
    title: "Open Space",
    desc: "List outdoor venues, parks, and recreational areas for events.",
    accent: "hover:border-emerald-500 hover:bg-emerald-50/60",
    iconBg: "bg-emerald-100 text-emerald-600",
    delay: "delay-250",
  },
  {
    type: "resort_owner",
    icon: Mountain,
    title: "Resort",
    desc: "Offer luxury stays, retreats, and vacation experiences.",
    accent: "hover:border-amber-500 hover:bg-amber-50/60",
    iconBg: "bg-amber-100 text-amber-600",
    delay: "delay-300",
  },
  {
    type: "apartment_owner",
    icon: Building2,
    title: "Apartments",
    desc: "List furnished apartments and serviced residences for short- or long-term rentals.",
    accent: "hover:border-sky-500 hover:bg-sky-50/60",
    iconBg: "bg-sky-100 text-sky-600",
    delay: "delay-350",
  },
  {
    type: "conference_hall_owner",
    icon: Users,
    title: "Conference Hall",
    desc: "Provide professional meeting rooms, boardrooms, and seminar spaces.",
    accent: "hover:border-rose-500 hover:bg-rose-50/60",
    iconBg: "bg-rose-100 text-rose-600",
    delay: "delay-400",
  },
];

function BusinessTypePage() {
  const navigate = useNavigate();

  const pick = (type: BusinessType) => {
    navigate({ to: "/business-signup" as any, search: { type } as any });
  };

  return (
    <section className="mx-auto flex min-h-[calc(100vh-80px)] max-w-3xl flex-col justify-center px-4 py-10">
      <div className="w-full rounded-3xl border border-border/60 bg-card shadow-elegant overflow-hidden p-8 sm:p-10">
        <div className="animate-fade-up mb-8 text-center">
          <Link to="/">
            <span className="font-display text-2xl font-bold text-primary">OyoBooking</span>
          </Link>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-foreground">
            What type of business do you own?
          </h1>
          <p className="mt-2 text-base text-muted-foreground">
            Select the category that best describes your property.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {OPTIONS.map((opt) => (
            <button
              key={opt.type}
              type="button"
              onClick={() => pick(opt.type)}
              className={`animate-pop ${opt.delay} group flex flex-col items-center gap-4 rounded-2xl border-2 border-border p-7 text-center transition-all duration-200 ${opt.accent} active:scale-[0.97]`}
            >
              <div className={`grid size-16 place-items-center rounded-2xl ${opt.iconBg} transition-transform duration-200 group-hover:scale-110`}>
                <opt.icon className="size-8" />
              </div>
              <div>
                <p className="text-xl font-bold text-foreground">{opt.title}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{opt.desc}</p>
              </div>
              <span className="mt-auto rounded-full border border-current px-5 py-1.5 text-xs font-semibold opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                Continue &rarr;
              </span>
            </button>
          ))}
        </div>

        <p className="animate-fade-in delay-450 mt-8 text-center text-sm text-muted-foreground">
          <button
            type="button"
            onClick={() => navigate({ to: "/signup" as any })}
            className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
          >
            <ArrowLeft className="size-3.5" /> Back to account selection
          </button>
        </p>
      </div>
    </section>
  );
}
